<!-- src/views/CasosDeExito.vue -->
<template>
  <section class="simulador-wrapper">

    <div class="simulador-container">
      <transition name="fade" mode="out-in">
        <div :key="currentSlide" class="slide">

          <!-- ===== SLIDE 1 ===== -->
          <div v-if="currentSlide === 1" class="row w-100 align-items-center">
            <div class="col-lg-5 col-12">
              <h1 class="titulo">{{ slides[0].titulo }}</h1>
              <p class="texto">{{ slides[0].texto }}</p>
            </div>
            <div class="col-lg-7 col-12 d-flex justify-content-center">
              <div class="img-wrapper">
                <img :src="resolveImage(slides[0].imagenFabrica)" class="img-fluid" />
                <img
                  :src="resolveImage(slides[0].imagenScada)"
                  class="scada-overlay monitor-blink"
                  @click="goToSlide(2)"
                />
              </div>
            </div>
          </div>

          <!-- ===== SLIDES 2 y 5 ===== -->
          <div v-else-if="[2,5].includes(currentSlide)" class="text-center">
            <img :src="resolveImage(current.imagen)" class="img-fluid mb-4" />
            <h2>{{ current.titulo }}</h2>
            <p>{{ current.texto }}</p>
            <button class="btn-iniciar" @click="goToSlide(current.botones[0].accion)">
              {{ current.botones[0].texto }}
            </button>
          </div>

          <!-- ===== SLIDES 3 y 6 ===== -->
          <div v-else-if="[3,6].includes(currentSlide)" class="text-center">
            <img :src="resolveImage(current.imagen)" class="img-fluid mb-4" />
            <div v-if="shouldShowNextButton(currentSlide)" class="fade-in">
              <button class="btn-grafana" @click="goToSlide(current.botones[0].accion)">
                {{ current.botones[0].texto }}
              </button>
            </div>
          </div>

          <!-- ===== SLIDES 4 y 7 ===== -->
          <div v-else-if="[4,7].includes(currentSlide)" class="text-center d-flex justify-content-center">
            <div class="img-wrapper-multi">
              <img :src="resolveImage(current.imagenFabrica)" class="img-fluid" />
              <img
                v-if="current.imagenScada"
                :src="resolveImage(current.imagenScada)"
                class="overlay scada"
              />
              <img
                v-if="current.imagenGrafana"
                :src="resolveImage(current.imagenGrafana)"
                :class="['overlay grafana', { 'monitor-blink': currentSlide === 4 }]"
                @click="goToSlide(current.botones[0].accion)"
              />
              <img
                v-if="current.imagenNotificacion"
                :src="resolveImage(current.imagenNotificacion)"
                class="overlay notificacion notificacion-blink"
                @click="goToSlide(current.botones[0].accion)"
              />
            </div>
          </div>

          <!-- ===== SLIDE 8 ===== -->
          <div v-else-if="currentSlide === 8" class="scrollable-slide">
            <div class="row align-items-center justify-content-center mb-5">
              <div class="col-lg-6 col-12">
                <img :src="resolveImage(current.animacion)" class="img-fluid" />
              </div>
              <div class="col-lg-6 col-12 text-center">
                <h2>{{ current.titulo }}</h2>
                <p>{{ current.texto }}</p>
                <div class="d-flex justify-content-center gap-3">
                  <button class="btn-grafana" @click="goToSlide(1)">
                    {{ current.botones[0].texto }}
                  </button>
                  <button class="btn-grafana" @click="goToContacto">
                    {{ current.botones[1].texto }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Casos de Éxito -->
            <div class="row text-center" style="background:#e5f8ff; padding:40px 0;">
              <div class="col-12 mb-4">
                <h2>¿Quieres ver casos de éxito reales?</h2>
              </div>
              <div v-for="caso in current.casosExito" :key="caso.id" class="col-md-3 col-12 mb-4">
                <div class="card h-100">
                  <div class="card-header">
                    <img :src="resolveImage(caso.imagen)" class="img-fluid" />
                  </div>
                  <div class="card-body">{{ caso.descripcionCorta }}</div>
                  <div class="card-footer">
                    <button class="btn btn-primary" data-bs-toggle="modal" :data-bs-target="'#'+caso.id">
                      Ver más
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </transition>
    </div>

    <!-- MODALES -->
    <div v-for="caso in slides[7]?.casosExito || []" :key="caso.id" class="modal" :id="caso.id">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h4>{{ caso.titulo }}</h4>
            <button class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">{{ caso.descripcionLarga }}</div>
          <div class="modal-footer">
            <button class="btn btn-danger" data-bs-dismiss="modal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script>
import { ref, computed } from "vue"
import { simuladorSlides } from "@/assets/data/data.ts"
import { useRouter } from "vue-router"
import { useSEO } from "@/composables/useSEO"

export default {
  props: {
    selectedLang: { type: String, default: "es" }
  },
  setup(props) {
    useSEO({
      title: 'Casos de Éxito',
      description: 'Proyectos reales de automatización e Industria 4.0 implementados por Frenkel Engineering. Descubre cómo transformamos plantas industriales con tecnología de punta.',
      path: '/casosdeexito',
    });
    const router = useRouter()
    const slides = computed(() => simuladorSlides[props.selectedLang])
    const currentSlide = ref(1)
    const showNextBtnSlide3 = ref(false)
    const showNextBtnSlide6 = ref(false)

    const current = computed(() => slides.value[currentSlide.value - 1])

    function resolveImage(path) {
      if (!path) return ""
      if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:") || path.startsWith("data:")) return path
      return new URL(path.replace("@/", "/src/"), import.meta.url).href
    }

    function goToSlide(index) {
      currentSlide.value = index
      if (index === 3) {
        showNextBtnSlide3.value = false
        setTimeout(() => showNextBtnSlide3.value = true, 3000)
      }
      if (index === 6) {
        showNextBtnSlide6.value = false
        setTimeout(() => showNextBtnSlide6.value = true, 3000)
      }
    }

    function shouldShowNextButton(id) {
      if (id === 3) return showNextBtnSlide3.value
      if (id === 6) return showNextBtnSlide6.value
      return false
    }

    function goToContacto() {
      router.push({ path: '/contacto' })
    }

    return {
      slides,
      currentSlide,
      current,
      resolveImage,
      goToSlide,
      shouldShowNextButton,
      goToContacto
    }
  }
}
</script>

<style scoped>
/* ===== FONDO GLOBAL ===== */
html, body {
  width: 100%;
  height: 100%;
  background-color: #f3e9dc;
  margin: 0;
  padding: 0;
}

.simulador-wrapper {
  width: 100%;
  min-height: 100vh;
  background-color: #f3e9dc;
  padding: 40px 20px;
}

.simulador-container {
  max-width: 1200px;
  margin: auto;
}

.slide { width: 100%; }

/* ===== SLIDE 1 OVERLAY ===== */
.img-wrapper {
  position: relative;
  width: 100%;
  max-width: 500px;
}

.scada-overlay {
  position: absolute;
  width: 35%;
  top: 10%;
  left: 8%;
}

/* ===== MULTI OVERLAY ===== */
.img-wrapper-multi {
  position: relative;
  width: 100%;
  max-width: 600px;
}

.overlay { position: absolute; width: 28%; }
.scada { top: 5%; left: 10%; }
.grafana { bottom: 10%; left: 15%; }
.notificacion { top: 5%; right: 10%; }

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .scada-overlay { width: 45%; top: 5%; left: 5%; }
  .overlay { width: 38%; }
  .scada { top: 2%; left: 5%; }
  .grafana { bottom: 5%; left: 10%; }
  .notificacion { top: 2%; right: 5%; }
}

/* ===== ANIMACIONES ===== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.5s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.monitor-blink,
.notificacion-blink { animation: blink 1.5s infinite; }

@keyframes blink { 0%,100% { opacity:1 } 50% { opacity:.4 } }

.scrollable-slide { overflow-y: auto; }
</style>